import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, PhoneOff, Volume2, X, Mic, AlertCircle, Clock } from '../icons';
import { getNLPPracticeVoiceToken, type NLPModelVariant } from '../../lib/nlp-practice';
import type { NLPModule, NLPPracticeStyle, NLPDifficulty, MiltonPatternFocus, MiltonContext, MiltonFeedbackTiming } from './nlpPracticeConstants';

const MAX_SESSION_SECONDS = 30 * 60; // OpenAI Realtime API hard limit: 30 minutes
const WARNING_SECONDS = 28 * 60; // Show warning at 28 minutes
const TOKEN_EXPIRY_SECONDS = 600; // Token expires after 10 min of inactivity

interface NLPPracticeVoiceProps {
  isOpen: boolean;
  onClose: () => void;
  module: NLPModule;
  practiceStyle: NLPPracticeStyle;
  difficulty: NLPDifficulty | null;
  onSessionEnd: (durationSeconds: number, usageLogId: string | null) => void;
  modelVariant?: NLPModelVariant;
  miltonOptions?: {
    patternFocus?: MiltonPatternFocus | null;
    practiceContext?: MiltonContext | null;
    feedbackTiming?: MiltonFeedbackTiming | null;
    targetDurationMinutes?: number | null;
  } | null;
}

type SessionStatus = 'idle' | 'connecting' | 'connected' | 'ended' | 'error';

export function NLPPracticeVoice({
  isOpen,
  onClose,
  module,
  practiceStyle,
  difficulty,
  onSessionEnd,
  modelVariant,
  miltonOptions,
}: NLPPracticeVoiceProps) {
  const [status, setStatus] = useState<SessionStatus>('idle');
  const [duration, setDuration] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [endReason, setEndReason] = useState<'user' | 'time_limit' | 'disconnected' | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const userEndedRef = useRef(false);
  const usageLogIdRef = useRef<string | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCPeerConnection | null>(null) as any;
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (dcRef.current) {
      try { dcRef.current.close(); } catch {}
      dcRef.current = null;
    }
    if (pcRef.current) {
      try { pcRef.current.close(); } catch {}
      pcRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  const startCall = useCallback(async () => {
    setStatus('connecting');
    setErrorMsg('');
    setDuration(0);
    setShowWarning(false);
    setEndReason(null);
    userEndedRef.current = false;

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = mediaStream;

      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      pc.ontrack = (e) => {
        if (audioRef.current) {
          audioRef.current.srcObject = e.streams[0];
          audioRef.current.play().catch(() => {});
        }
      };

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
          if (!userEndedRef.current) {
            handleServerDisconnect();
          }
        }
      };

      const dc = pc.createDataChannel('oai-events');
      dcRef.current = dc;

      dc.onopen = () => {
        setStatus('connected');
        startTimeRef.current = Date.now();
        timerRef.current = setInterval(() => {
          const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
          setDuration(elapsed);
          if (elapsed >= WARNING_SECONDS && !showWarning) {
            setShowWarning(true);
          }
          if (elapsed >= MAX_SESSION_SECONDS) {
            handleTimeLimitReached();
          }
        }, 1000);
      };

      dc.onclose = () => {
        if (!userEndedRef.current) {
          handleServerDisconnect();
        }
      };

      dc.onerror = () => {
        if (!userEndedRef.current) {
          setErrorMsg('Connection error');
          setStatus('error');
        }
      };

      mediaStream.getTracks().forEach((track) => {
        pc.addTrack(track, mediaStream);
      });

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const isLiveModel = modelVariant === 'gpt-live-1';

      if (isLiveModel) {
        // gpt-live-1: send the SDP offer to the edge function, get back the answer SDP
        const { client_secret: _secret, usage_log_id, answer_sdp } = await getNLPPracticeVoiceToken(
          module,
          practiceStyle,
          difficulty,
          modelVariant,
          offer.sdp,
          miltonOptions || undefined,
        );
        usageLogIdRef.current = usage_log_id;

        if (!answer_sdp) {
          throw new Error('No answer SDP returned from Live session');
        }
        await pc.setRemoteDescription({ type: 'answer', sdp: answer_sdp });
      } else {
        // gpt-realtime-2.1: get client secret, then exchange SDP via /v1/realtime/calls
        const { client_secret, usage_log_id } = await getNLPPracticeVoiceToken(
          module,
          practiceStyle,
          difficulty,
          modelVariant,
          undefined,
          miltonOptions || undefined,
        );
        usageLogIdRef.current = usage_log_id;

        const sdpRes = await fetch(`https://api.openai.com/v1/realtime/calls`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/sdp',
            Authorization: `Bearer ${client_secret}`,
          },
          body: offer.sdp,
        });

        if (!sdpRes.ok) {
          const errText = await sdpRes.text();
          throw new Error(`Realtime connection failed (${sdpRes.status}): ${errText}`);
        }

        const answerSdp = await sdpRes.text();
        await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
      }
    } catch (err: any) {
      const msg = err?.name === 'NotAllowedError'
        ? 'Microphone access denied. Please allow microphone access and try again.'
        : err?.message || 'Failed to start voice session';
      setErrorMsg(msg);
      setStatus('error');
      cleanup();
    }
  }, [module, practiceStyle, difficulty, cleanup, showWarning, modelVariant, miltonOptions]);

  const handleTimeLimitReached = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    const callDuration = startTimeRef.current
      ? Math.floor((Date.now() - startTimeRef.current) / 1000)
      : 0;
    setEndReason('time_limit');
    setStatus('ended');
    cleanup();
    if (callDuration > 0) {
      onSessionEnd(callDuration, usageLogIdRef.current);
    }
  }, [cleanup, onSessionEnd]);

  const handleServerDisconnect = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    const callDuration = startTimeRef.current
      ? Math.floor((Date.now() - startTimeRef.current) / 1000)
      : 0;
    // If the session was near the 30-min mark, it was the time limit; otherwise it was an unexpected disconnect
    if (callDuration >= MAX_SESSION_SECONDS - 60) {
      setEndReason('time_limit');
    } else {
      setEndReason('disconnected');
    }
    setStatus('ended');
    cleanup();
    if (callDuration > 0) {
      onSessionEnd(callDuration, usageLogIdRef.current);
    }
  }, [cleanup, onSessionEnd]);

  const handleUserEndCall = useCallback(() => {
    userEndedRef.current = true;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    const callDuration = startTimeRef.current
      ? Math.floor((Date.now() - startTimeRef.current) / 1000)
      : 0;
    setEndReason('user');
    setStatus('ended');
    cleanup();
    if (callDuration > 0) {
      onSessionEnd(callDuration, usageLogIdRef.current);
    }
  }, [cleanup, onSessionEnd]);

  const handleClose = useCallback(() => {
    if (status === 'connected') {
      handleUserEndCall();
    }
    onClose();
  }, [status, handleUserEndCall, onClose]);

  const handleStartNew = useCallback(() => {
    setEndReason(null);
    setErrorMsg('');
    setShowWarning(false);
    startCall();
  }, [startCall]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const formatRemaining = (seconds: number) => {
    const remaining = Math.max(0, MAX_SESSION_SECONDS - seconds);
    const m = Math.floor(remaining / 60);
    const s = remaining % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="fixed bottom-4 right-4 w-[380px] bg-white rounded-2xl shadow-2xl border border-cream-200 flex flex-col z-50 overflow-hidden"
        >
          <audio ref={audioRef} autoPlay className="hidden" />
          <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-sage-600 to-sage-700 text-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl">
                <Volume2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">NLP Practice Coach</h3>
                <p className="text-xs text-sage-100">
                  Voice Session{modelVariant ? ` · ${modelVariant}` : ''}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 flex flex-col items-center">
            {status === 'idle' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
                <div className="w-20 h-20 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-5">
                  <Mic className="w-10 h-10 text-sage-600" />
                </div>
                <p className="text-sm text-chocolate-600 font-medium mb-2">NLP Voice Practice</p>
                <p className="text-xs text-chocolate-400 mb-2 max-w-[260px]">
                  Start a voice conversation with your NLP practice coach. Speak naturally and the AI will respond.
                </p>
                <p className="text-xs text-chocolate-300 mb-6 max-w-[260px] flex items-center justify-center gap-1">
                  <Clock className="w-3 h-3" />
                  Sessions last up to 30 minutes
                </p>
                <button
                  onClick={startCall}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-sage-600 text-white rounded-xl hover:bg-sage-700 transition-colors font-medium text-sm"
                >
                  <Phone className="w-4 h-4" />
                  Start Voice Session
                </button>
              </motion.div>
            )}

            {status === 'connecting' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-4">
                <div className="w-20 h-20 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-5 animate-pulse">
                  <Phone className="w-10 h-10 text-sage-600" />
                </div>
                <p className="text-sm text-chocolate-600 font-medium">Connecting...</p>
                <p className="text-xs text-chocolate-400 mt-1">Setting up your voice session</p>
              </motion.div>
            )}

            {status === 'connected' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center w-full">
                <div className="relative w-20 h-20 mx-auto mb-5">
                  <div className="absolute inset-0 bg-sage-200 rounded-full animate-ping opacity-30" />
                  <div className="relative w-20 h-20 bg-sage-500 rounded-full flex items-center justify-center">
                    <Volume2 className="w-10 h-10 text-white" />
                  </div>
                </div>
                <p className="text-2xl font-mono font-semibold text-chocolate-800 mb-1">
                  {formatTime(duration)}
                </p>
                <p className="text-xs text-sage-600 font-medium mb-1">Session in progress</p>
                <p className="text-xs text-chocolate-300 mb-4 flex items-center justify-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatRemaining(duration)} remaining
                </p>

                {showWarning && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5"
                  >
                    <p className="text-xs text-amber-700 font-medium">
                      Your session will end in {Math.ceil((MAX_SESSION_SECONDS - duration) / 60)} minutes.
                    </p>
                    <p className="text-xs text-amber-600 mt-0.5">
                      You can start a new session right after to keep practicing.
                    </p>
                  </motion.div>
                )}

                <button
                  onClick={handleUserEndCall}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-warm-600 text-white rounded-xl hover:bg-warm-700 transition-colors font-medium text-sm"
                >
                  <PhoneOff className="w-4 h-4" />
                  End Session
                </button>
              </motion.div>
            )}

            {status === 'ended' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center w-full">
                {endReason === 'user' && (
                  <>
                    <div className="w-20 h-20 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-5">
                      <PhoneOff className="w-10 h-10 text-sage-600" />
                    </div>
                    <p className="text-sm text-chocolate-600 font-medium mb-1">Session ended</p>
                    <p className="text-xs text-chocolate-400 mb-6">
                      Practice time: {formatTime(duration)}
                    </p>
                  </>
                )}

                {endReason === 'time_limit' && (
                  <>
                    <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-5">
                      <Clock className="w-10 h-10 text-amber-600" />
                    </div>
                    <p className="text-sm text-chocolate-600 font-medium mb-1">30-minute session limit reached</p>
                    <p className="text-xs text-chocolate-400 mb-1">
                      Practice time: {formatTime(duration)}
                    </p>
                    <p className="text-xs text-chocolate-400 mb-6 max-w-[260px]">
                      Voice sessions have a 30-minute limit. Start a new session to continue practicing.
                    </p>
                  </>
                )}

                {endReason === 'disconnected' && (
                  <>
                    <div className="w-20 h-20 bg-warm-100 rounded-full flex items-center justify-center mx-auto mb-5">
                      <AlertCircle className="w-10 h-10 text-warm-600" />
                    </div>
                    <p className="text-sm text-chocolate-600 font-medium mb-1">Connection lost</p>
                    <p className="text-xs text-chocolate-400 mb-1">
                      Practice time: {formatTime(duration)}
                    </p>
                    <p className="text-xs text-chocolate-400 mb-6 max-w-[260px]">
                      The connection was interrupted. Try starting a new session.
                    </p>
                  </>
                )}

                <button
                  onClick={handleStartNew}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-sage-600 text-white rounded-xl hover:bg-sage-700 transition-colors font-medium text-sm"
                >
                  <Phone className="w-4 h-4" />
                  Start New Session
                </button>
              </motion.div>
            )}

            {status === 'error' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-4">
                <p className="text-sm text-warm-600 mb-3">{errorMsg || 'Connection failed'}</p>
                <button
                  onClick={startCall}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-sage-600 text-white rounded-xl hover:bg-sage-700 transition-colors font-medium text-sm"
                >
                  <Phone className="w-4 h-4" />
                  Try Again
                </button>
              </motion.div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
